/**
 * TKP-237 t.1–t.8 — taslak satırının onay ekranındaki görünümü: İşlem, Filtre (iki satır), Mevcut Değer, Yeni Değer. Saf: çeviri (`t`) ve
 * yerel ad (`tr`) dışarıdan verilir. Toplu işlemin (aynı `batch.id`) satırları tek görünümde birleşir (karar 2026-11-01).
 *
 * Kurallar (kullanıcı listesi 2026-11-01):
 *  - Kayıt işlemleri: Ekleme / Düzenleme / Silme / Arşive Alma; Filtre 1. satır seviye adı (Birim / Süreç / Veri / İlgili Kişi).
 *  - Envanter özelliği: İşlem "Düzenleme"; Filtre "{Seviye} Envanter Özelliği - Ekleme Yap|Üzerine Yaz|Kaldır";
 *    2. satır yol + (toplu işlemde seçili liste) + özellik ADI (değeri yazılmaz).
 *  - Mevcut değer düzenleme / silme / arşive alma / üzerine yaz / kaldır işlemlerinde dolar; Yeni değer yalnız girilen değer.
 */
import { summarize } from "./approval";

const id = (v) => (v && v._id ? String(v._id) : v ? String(v) : "");
const uniq = (list) => [...new Set(list.filter((v) => v && v !== "—"))];

/** Özellik türü / bölüm / alan → bölüm adı anahtarı (envanter özellikleri panelindeki başlıklar). */
export const FEATURE_LABEL = {
  DatasourceDirectCollectMethod: "direct-collect-method.text",
  DatasourceOtherCollectMethod: "other-collect-method.text",
  DatasourcePhysicalStorage: "physical-storage.text",
  DatasourcePhysicalArchive: "physical-archive.text",
  DatasourceElectronicStorage: "electronic-storage.text",
  DatasourceElectronicArchive: "electronic-archive.text",
  DataDisposalMethod: "disposal-method.text",
  DataAccessAndRestriction: "accessing-persons.text",
  DatasubjectDatasubjectRight: "datasubject-right.text",
  PROCESSING_LEGAL: "processing.text",
  TRANSFER: "transfering.text",
};
export const SECTION_LABEL = {
  directCollectMethods: "direct-collect-method.text",
  otherCollectMethods: "other-collect-method.text",
  physicalStorages: "physical-storage.text",
  physicalArchives: "physical-archive.text",
  electronicStorages: "electronic-storage.text",
  electronicArchives: "electronic-archive.text",
  disposalMethods: "disposal-method.text",
  disposalPeriod: "disposal-method.periodic",
  accessAndRestrictions: "accessing-persons.text",
  datasubjectRights: "datasubject-right.text",
  processingLegals: "processing.text",
  transferings: "transfering.text",
  measures: "inventory.node-form.measures-title",
  autoProcessing: "process-data.inventory-panel.processing-features",
};
/** Düğüm / satır güncellemesinde özellik alanları (bunlardan başka alan değiştiyse kayıt düzenlemesidir). */
const FIELD_SECTION = { feature: "disposalPeriod", disposalPeriod: "disposalPeriod", autoProcessing: "autoProcessing", aiProcessing: "autoProcessing", administrativeMeasures: "measures", technicalMeasures: "measures" };
const ENTITY_LEVEL = { DEPARTMENT: "DEPARTMENT", PROCESS: "PROCESS", NODE: "NODE", NODE_DATASUBJECT: "ROW" };
const PATH_KEYS = { DEPARTMENT: ["department"], PROCESS: ["department", "process"], NODE: ["department", "process", "data"], ROW: ["department", "process", "data", "datasubject"] };

const changed = (edit) => (edit.changedFields || []).filter((f) => !["scope", "header", "pagination", "organization", "createdAt", "updatedAt", "__v"].includes(f));
const isFeatureUpdate = (edit) => edit.operation === "UPDATE" && ["NODE", "NODE_DATASUBJECT"].includes(edit.entityType) && changed(edit).length > 0 && changed(edit).every((f) => f in FIELD_SECTION);
/** Envanter özelliği satırı mı (kayıt işlemi değil)? */
export const isFeatureEdit = (edit) => ["FEATURE", "PROCESSING_LEGAL", "TRANSFER"].includes(edit.entityType) || isFeatureUpdate(edit);

/** Özelliğin seviyesi: ilgili kişi satırına yazıldıysa İlgili Kişi, veriye yazıldıysa Veri. */
const featureLevel = (edit) => {
  if (isFeatureUpdate(edit)) return ENTITY_LEVEL[edit.entityType];
  if (edit.entityType === "PROCESSING_LEGAL" || edit.entityType === "TRANSFER") return "ROW";
  const ctx = edit.context || {};
  return ctx.datasubject ? "ROW" : "NODE";
};
const featureKey = (edit) => (isFeatureUpdate(edit) ? SECTION_LABEL[FIELD_SECTION[changed(edit)[0]]] : FEATURE_LABEL[edit.featureKind] || FEATURE_LABEL[edit.entityType]);
const featureOp = (edit) => (edit.operation === "CREATE" ? "ADD" : edit.operation === "DELETE" ? "REMOVE" : "OVERRIDE");

const pathOf = (names, level, tr) =>
  (PATH_KEYS[level] || [])
    .map((k) => tr(names && names[k]))
    .filter(Boolean)
    .join(" > ");

/** Kayıt işleminin adı: arşivleme ayrı ad taşır. */
const entityOperation = (edit) => {
  if (edit.operation === "UPDATE" && changed(edit).includes("archived")) return edit.after && edit.after.archived ? "ARCHIVE" : "UNARCHIVE";
  return edit.operation;
};

/** Kaydın kendi adı (bağlamın en derin seviyesi; yoksa taslaktaki ad). */
const ownName = (edit, tr) => {
  const ctx = edit.context || {};
  const key = { DEPARTMENT: "department", PROCESS: "process", NODE: "data", NODE_DATASUBJECT: "datasubject" }[edit.entityType];
  return tr(ctx[key]) || tr(edit.after && (edit.after.name || (edit.after.libraryItem && edit.after.libraryItem.name))) || tr(edit.before && edit.before.name) || "";
};

/** Saklama süresi gibi nesne alanlar okunur biçimde ("6 ay"); diğerleri summarize'ın gösterimi. */
const plain = (edit, side, field, shown, t) => {
  const raw = edit[side] && edit[side][field];
  const months = raw && typeof raw === "object" && typeof raw.disposalPeriod === "number" ? raw.disposalPeriod : field === "disposalPeriod" && typeof raw === "number" ? raw : null;
  if (months !== null) return t("approval.view.months", { count: months });
  if (typeof raw === "boolean") return t(raw ? "common.yes" : "common.no");
  return shown;
};

/**
 * TKP-240 b.6 — alanın kayıtta YOKKEN anlamı (şema varsayılanı). Form bu alanları her güncellemede gönderir; eski kayıtta alan yoksa
 * değişmemiş değer "değişti" sayılıp yeni değer sütununa "Evet" yazılıyordu (birim adını düzenleyince "Yeni ad, Evet").
 */
const ABSENT_MEANS = { processAllowed: true, completed: false, archived: false };
const normalized = (field, v) => (v === undefined || v === null ? (field in ABSENT_MEANS ? ABSENT_MEANS[field] : v) : v);
const reallyChanged = (edit, row) => {
  const before = edit.before && edit.before[row.field];
  const after = edit.after && edit.after[row.field];
  if (typeof before === "object" || typeof after === "object") return true;
  return edit.operation !== "UPDATE" || normalized(row.field, before) !== normalized(row.field, after);
};

/** Değer sütunları: yalnız değerler (alan adı / işlem bilgisi tekrar edilmez), ham kimlik görünmez (summarize). */
const values = (edit, tr, t) => {
  const rows = summarize(edit).filter((r) => reallyChanged(edit, r));
  return { before: uniq(rows.map((r) => plain(edit, "before", r.field, r.before, t))), after: uniq(rows.map((r) => plain(edit, "after", r.field, r.after, t))) };
};

/** Tekil taslak satırının görünümü. */
export const editView = (edit, { t, tr }) => {
  const ctx = edit.context || {};
  if (isFeatureEdit(edit)) {
    const level = featureLevel(edit);
    const op = featureOp(edit);
    const record = tr(ctx.record);
    const v = values(edit, tr, t);
    const before = op === "ADD" ? [] : op === "REMOVE" ? uniq([record, ...v.before]) : v.before;
    const after = op === "REMOVE" ? [] : op === "ADD" ? uniq([record || v.after[0]]) : v.after;
    const feature = featureKey(edit) ? t(featureKey(edit)) : "";
    return {
      operation: t("approval.view.op.UPDATE"),
      filter: [t("approval.view.feature-line", { level: t(`approval.view.level.${level}`), op: t(`approval.view.feature-op.${op}`) }), [pathOf(ctx, level, tr), feature].filter(Boolean).join(" - ")],
      before,
      after,
    };
  }
  const level = ENTITY_LEVEL[edit.entityType];
  if (!level) {
    // başvuru, veri ihlali, sözlük tanımları: eski görünüm (tür adı + bağlam)
    const v = values(edit, tr, t);
    return { operation: t(`approval.operation.${edit.operation}`), filter: [t(`approval.entity-type.${edit.entityType}`), Object.values(ctx).map(tr).filter(Boolean).join(" > ")].filter(Boolean), before: v.before, after: v.after };
  }
  const op = entityOperation(edit);
  const name = ownName(edit, tr);
  const v = values(edit, tr, t);
  const before = op === "CREATE" ? [] : op === "DELETE" || op === "ARCHIVE" || op === "UNARCHIVE" ? uniq([name]) : v.before;
  const after = op === "DELETE" ? [] : op === "CREATE" ? uniq([name]) : op === "ARCHIVE" ? [t("approval.view.archived")] : op === "UNARCHIVE" ? [t("approval.view.unarchived")] : v.after;
  return { operation: t(`approval.view.op.${op}`), filter: [t(`approval.view.level.${level}`), pathOf(ctx, level, tr)].filter(Boolean), before, after };
};

/**
 * TKP-244 (müşteri 2026-11-03): toplu işlemin seçimi filtrede okunur — veri başına ilgili kişileri ("Veri (İlgili Kişi, …)"), taslak
 * satırlarının bağlamından; "Ayrıntı" satırları gösterilmez. Onaylayan hangi verinin hangi ilgili kişisine yazılacağını buradan görür.
 */
export const batchSelection = (edits = [], tr) => {
  const order = [];
  const byData = {};
  for (const edit of edits) {
    const ctx = (edit && edit.context) || {};
    const data = tr(ctx.data);
    if (!data) continue;
    if (!byData[data]) {
      byData[data] = [];
      order.push(data);
    }
    const datasubject = tr(ctx.datasubject);
    if (datasubject && !byData[data].includes(datasubject)) byData[data].push(datasubject);
  }
  return order.map((data) => (byData[data].length ? `${data} (${byData[data].join(", ")})` : data));
};
/** Filtre satırına sığan kısım (en çok `max` karakter), fazlası "+N"; tamamı ayrıca açılır. */
export const clipSelection = (entries = [], max = 400) => {
  const out = [];
  let length = 0;
  for (const entry of entries) {
    if (out.length && length + entry.length + 2 > max) return `${out.join("; ")} +${entries.length - out.length}`;
    out.push(entry);
    length += entry.length + 2;
  }
  return out.join("; ");
};

/** Toplu işlemin (aynı batch) birleşik görünümü. */
export const batchView = (batch, edits, { t, tr }) => {
  const childViews = edits.map((e) => editView(e, { t, tr }));
  const before = uniq(childViews.flatMap((v) => v.before));
  const after = uniq(childViews.flatMap((v) => v.after));
  const path = batch.path || {};
  if (batch.kind === "REMOVE_DATASUBJECTS") {
    // TKP-242 q.4: "İlgili Kişileri … Tüm Verilerinden Kaldır / Değiştir" — mevcut: kaldırılanlar, yeni: yerine geçen (varsa)
    const level = batch.level === "PROCESS" ? "PROCESS" : "DEPARTMENT";
    return { operation: t("approval.view.op.UPDATE"), filter: [t(`approval.view.remove-line.${level}`), pathOf(path, level, (v) => v)], before: batch.targets ? batch.targets.split(", ") : before, after: batch.replacement ? [batch.replacement] : [] };
  }
  if (batch.kind === "ASSIGN_DATASUBJECTS") {
    return { operation: t("approval.view.op.ASSIGN"), filter: [t("approval.view.assign-line"), pathOf(path, "PROCESS", (v) => v)], before: [], after: batch.targets ? batch.targets.split(", ") : after };
  }
  const level = batch.level;
  const op = ["ADD", "OVERRIDE", "REMOVE"].includes(batch.editOption) ? batch.editOption : "ADD";
  const feature = SECTION_LABEL[batch.section] ? t(SECTION_LABEL[batch.section]) : "";
  // TKP-244: birimde "tüm veriler" (seçim yok); süreç / veri / ilgili kişi seviyesinde seçilen veri – ilgili kişi çiftleri. İlgili kişi
  // seviyesinde tek çift yolun kendisidir, yazılmaz. Bağlam yoksa (eski satır) işlemle gelen ad listesi.
  const selection = level === "DEPARTMENT" ? [] : batchSelection(edits, tr);
  // veri seviyesinde veri adı yolda: yalnız ilgili kişiler
  const ofNode = level === "NODE" && selection.length === 1 && path.data && selection[0].startsWith(`${path.data} (`) ? selection[0].slice(path.data.length + 2, -1).split(", ") : null;
  const shown = ofNode || (level === "ROW" && selection.length < 2 ? [] : selection);
  const scope = level === "DEPARTMENT" ? t("approval.view.department-scope") : shown.length ? clipSelection(shown) : level === "ROW" ? "" : batch.targets || "";
  return {
    operation: t("approval.view.op.UPDATE"),
    filter: [t("approval.view.feature-line", { level: t(`approval.view.level.${level}`), op: t(`approval.view.feature-op.${op}`) }), [pathOf(path, level, (v) => v), scope, feature].filter(Boolean).join(" - ")],
    selection: shown,
    before: op === "ADD" ? [] : before,
    after: op === "REMOVE" ? [] : after,
  };
};

/**
 * Satırları toplu işleme göre grupla: aynı `batch.id` tek kalem (ilk görüldüğü sırada); tek satırlık toplu işlem de grup olarak kalır
 * (seviye ve kip bilgisi işlemden gelir). Döner: `{ key, batch | null, edits }[]`.
 */
export const groupEdits = (edits = []) => {
  const out = [];
  const byBatch = {};
  for (const edit of edits || []) {
    const b = edit && edit.batch && edit.batch.id ? edit.batch : null;
    if (!b) {
      out.push({ key: id(edit._id), batch: null, edits: [edit] });
      continue;
    }
    if (!byBatch[b.id]) {
      byBatch[b.id] = { key: `batch-${b.id}`, batch: b, edits: [] };
      out.push(byBatch[b.id]);
    }
    byBatch[b.id].edits.push(edit);
  }
  return out;
};
